import { Injectable } from '@angular/core';
import { NavigationExtras, Router } from '@angular/router';
import { UserDb } from 'lib/cosmos/user/userDb.js';
import { routingModulePaths } from 'src/app/app-routing-module-helper.js';
import { ITypeRoute, ITypeRouteAction } from 'src/app/pages/type-routes/type-route.models.js';
import { TypeRouteService } from 'src/app/pages/type-routes/type-route.service.js';
import { IAppRoutes } from 'src/app/routes/app-routes.js';

export class NavigateResult {
	constructor(private navigateMethod: (value: string) => void, private url: string) {}

	navigate(): void {
		this.navigateMethod(this.prepareUrl(this.url));
		return;
	}

	getUrl(): string {
		return this.prepareUrl(this.url);
	}

	private prepareUrl(url: string): string {
		if (!url) {
			throw Error('Url is not set!');
		}

		let processedUrl: string = url;

		if (!this.url.startsWith('/')) {
			processedUrl = '/' + processedUrl;
		}

		return processedUrl;
	}
}

@Injectable({
	providedIn: 'root'
})
export class NavigateService {
	public readonly appRoutes: IAppRoutes = this.typeRouteService.appRoutes;

	constructor(public router: Router, private typeRouteService: TypeRouteService) {}

	dashboardPage(userType: UserDb.UserType | undefined): NavigateResult {
		if (!userType) {
			return this.getNavigateResult('/');
		}
		if (userType === 'client') {
			return this.clientDashboard();
		}
		return this.trainerDashboard();
	}

	notFoundPage(): NavigateResult {
		return this.forRoute('/404', {
			skipLocationChange: true
		});
	}

	errorPage(): NavigateResult {
		return this.forRoute('/error', {
			skipLocationChange: true
		});
	}

	forgottenPasswordPage(): NavigateResult {
		return this.getNavigateResult(routingModulePaths.forgottenPassword);
	}

	loginPage(): NavigateResult {
		return this.getNavigateResult(routingModulePaths.login);
	}

	logoutPage(): NavigateResult {
		return this.getNavigateResult(routingModulePaths.logout);
	}

	signupPage(): NavigateResult {
		return this.getNavigateResult(routingModulePaths.signup);
	}

	subscribePage(): NavigateResult {
		return this.actionRoute((routes) => routes.profileRoutes.view.children.subscription);
	}

	emptyPage(): NavigateResult {
		return this.getNavigateResult(routingModulePaths.empty);
	}

	insufficientPermissionsPage(): NavigateResult {
		return this.getNavigateResult(routingModulePaths.insufficientPermissions, {
			skipLocationChange: true
		});
	}

	trainerAccountReadyPage(): NavigateResult {
		return this.actionRoute((routes) => routes.profileRoutes.account.children.ready);
	}

	trainerDashboard(): NavigateResult {
		return this.actionRoute((routes) => routes.trainerHomeRoutes.dashboard.children.main);
	}

	clientDashboard(): NavigateResult {
		return this.actionRoute((routes) => routes.clientRoutes.dashboard.children.overview);
	}

	actionRoute(
		actionSelection: (appRoutes: IAppRoutes) => ITypeRouteAction<any>,
		primaryId?: string,
		secondaryId?: string,
		tertiaryId?: string
	): NavigateResult {
		const result = this.getRouteAction(
			actionSelection(this.typeRouteService.appRoutes),
			primaryId,
			secondaryId,
			tertiaryId
		);
		return result;
	}

	typeRoute(typeSelection: (appRoutes: IAppRoutes) => ITypeRoute<any, any>): string {
		return this.getRouteType(typeSelection(this.typeRouteService.appRoutes));
	}

	private forRoute(route: string, navigationExtras?: NavigationExtras): NavigateResult {
		return this.getNavigateResult(route, navigationExtras);
	}

	private typePage(
		mainType: string,
		type: string,
		action: string,
		primaryId?: string,
		secondaryId?: string,
		tertiaryId?: string
	): NavigateResult {
		let url: string = `/auth/${mainType}/${type}/${action}`;

		if (primaryId) {
			url += `/${primaryId}`;
		}

		if (secondaryId) {
			url += `/${secondaryId}`;
		}

		if (tertiaryId) {
			url += `/${tertiaryId}`;
		}

		return this.forRoute(url);
	}

	private getRouteType(type: ITypeRoute<any, any>): string {
		const resolvedType = this.typeRouteService.getResolvedRoutes()[type.uniqueId];

		if (!resolvedType) {
			throw Error(`Missing route for type with unique id '${type.uniqueId}'`);
		}

		return resolvedType.type;
	}

	private getRouteAction(
		action: ITypeRouteAction<any>,
		primaryId?: string,
		secondaryId?: string,
		tertiaryId?: string
	): NavigateResult {
		const resolvedAction = this.typeRouteService.getResolvedActions()[action.uniqueId];

		if (!resolvedAction) {
			throw Error(`Missing route for action unique id '${action.uniqueId}'`);
		}

		return this.typePage(
			resolvedAction.mainPath,
			resolvedAction.typePath,
			resolvedAction.actionPath,
			primaryId,
			secondaryId,
			tertiaryId
		);
	}

	private getNavigateResult(route: string, navigationExtras?: NavigationExtras): NavigateResult {
		return new NavigateResult(
			(xRoute) => this.router.navigate([xRoute.toLowerCase()], navigationExtras),
			route.toLowerCase()
		);
	}
}
